import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useHoneypot } from '@/hooks/useHoneypot';
import { getBookExtra } from '@/content/bookExtras';
import BookCover from '@/components/BookCover';
import NotFound from './NotFound';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BookExtra = () => {
  const { slug } = useParams();
  const extra = getBookExtra(slug);
  const hp = useHoneypot();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!extra) return <NotFound />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const cleanEmail = email.trim().toLowerCase();
    if (!firstName.trim()) return setError('Bitte gib deinen Vornamen ein.');
    if (!EMAIL_RE.test(cleanEmail)) return setError('Bitte gib eine gültige E-Mail-Adresse ein.');
    if (!consent) return setError('Bitte bestätige die Datenschutzhinweise.');
    setLoading(true);
    const { error: fnError } = await supabase.functions.invoke('book-extra-signup', {
      body: { first_name: firstName.trim(), email: cleanEmail, slug: extra.slug, consent, website: hp.value, landing_url: window.location.href },
    });
    setLoading(false);
    if (fnError) {
      const status = (fnError as { context?: Response }).context?.status;
      setError(
        status === 429
          ? 'Zu viele Versuche. Bitte probier es später noch einmal.'
          : 'Das hat leider nicht geklappt. Bitte versuch es gleich noch einmal oder schreib mir an juergen@klanert.com.',
      );
      return;
    }
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO title={`${extra.title} – Extra zum Buch Unbequem Gesund`} description={extra.description} noindex />
      <Header />
      <main id="main-content" className="pt-24 md:pt-32 pb-20">
        <div className="container-blog max-w-xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <BookCover className="!w-14 shrink-0" />
            <div>
              <p className="text-waldgruen font-medium tracking-[0.2em] uppercase text-typography-meta">
                Dein Extra zum Buch Unbequem Gesund
              </p>
              <p className="text-graphit/60 text-sm mt-1">Kein Hype. Nur Evidenz.</p>
            </div>
          </div>

          <h1 className="text-typography-heading text-graphit mb-4">{extra.title}</h1>
          <p className="text-graphit/80 text-typography-body font-light leading-[1.7] mb-10">{extra.description}</p>

          {done ? (
            <div role="status" className="rounded-2xl border border-waldgruen/40 bg-primary/5 p-6">
              <p className="text-graphit font-medium">Check dein Postfach. Der Zugang ist unterwegs.</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-5">
              {hp.field}
              <div className="space-y-2">
                <Label htmlFor="be-first">Vorname</Label>
                <Input id="be-first" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="be-email">E-Mail</Label>
                <Input id="be-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="be-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-1" />
                <Label htmlFor="be-consent" className="font-light leading-relaxed text-graphit/80">
                  Ich möchte den Zugang per E-Mail erhalten und habe die{' '}
                  <Link to="/privacy#buch-extras" className="underline underline-offset-2">Datenschutzhinweise</Link> gelesen.
                </Label>
              </div>
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Einen Moment …' : 'Jetzt Zugang per E-Mail holen'}
              </Button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookExtra;
